import { eventCatalog, type EventType } from './catalog.generated';
export type FlowsheetColumn = {
  key: string;
  label: string;
  eventType: EventType | `CUSTOM.${string}`;
  schemaVersion?: number;
  unit?: string;
  path: string;
  format: 'number' | 'bp' | 'text';
  subject: 'MOTHER' | 'FETUS' | 'NEWBORN';
};
export type FlowsheetDefinition = {
  id: string;
  version: number;
  rowStrategy: 'PER_ENCOUNTER' | 'PER_TIMESTAMP_BUCKET' | 'PER_EVENT';
  bucketSeconds?: number;
  sort: 'ASC' | 'DESC';
  columns: FlowsheetColumn[];
};
export const prenatalDefinition: FlowsheetDefinition = {
  id: 'prenatal',
  version: 1,
  rowStrategy: 'PER_ENCOUNTER',
  sort: 'DESC',
  columns: [
    {
      key: 'weight_gain',
      label: 'Total weight change (lb / kg)',
      eventType: 'VITALS.WEIGHT',
      path: 'grams',
      format: 'number',
      subject: 'MOTHER',
    },
    ...[
      { key: 'labs', label: 'Labs drawn', path: 'labs_drawn' },
      { key: 'rhig', label: 'RhIG administration', path: 'rhig_given' },
      { key: 'exam', label: 'Internal exam', path: 'internal_exam' },
      { key: 'urine_other', label: 'Other urine', path: 'urine_other' },
      { key: 'education', label: 'Education', path: 'education' },
      { key: 'orders', label: 'Orders', path: 'orders' },
    ].map((c) => ({
      ...c,
      eventType: 'PRENATAL.VISIT_DETAILS' as const,
      format: 'text' as const,
      subject: 'MOTHER' as const,
    })),
    { key: 'bp', label: 'BP (mmHg)', eventType: 'VITALS.BP', path: 'systolic', format: 'bp', subject: 'MOTHER' },
    {
      key: 'weight',
      label: 'Weight (lb / kg)',
      eventType: 'VITALS.WEIGHT',
      path: 'grams',
      format: 'number',
      subject: 'MOTHER',
    },
    { key: 'pulse', label: 'Pulse', eventType: 'VITALS.PULSE', path: 'rate', format: 'number', subject: 'MOTHER' },
    {
      key: 'temp',
      label: 'Temperature (°C)',
      eventType: 'VITALS.TEMP',
      path: 'value_c',
      format: 'text',
      subject: 'MOTHER',
    },
    {
      key: 'fh',
      label: 'Fundal height',
      eventType: 'PRENATAL.FUNDAL_HEIGHT',
      path: 'cm',
      format: 'text',
      subject: 'MOTHER',
    },
    { key: 'fht', label: 'FHT', eventType: 'PRENATAL.FHT', path: 'bpm', format: 'text', subject: 'FETUS' },
    {
      key: 'presentation',
      label: 'Presentation',
      eventType: 'PRENATAL.FETAL_PRESENTATION',
      path: 'presentation',
      format: 'text',
      subject: 'FETUS',
    },
    {
      key: 'movement',
      label: 'Movement',
      eventType: 'PRENATAL.FETAL_MOVEMENT',
      path: 'value',
      format: 'text',
      subject: 'FETUS',
    },
    { key: 'edema', label: 'Edema', eventType: 'PRENATAL.EDEMA', path: 'value', format: 'text', subject: 'MOTHER' },
    {
      key: 'urine',
      label: 'Urine protein / glucose',
      eventType: 'LAB.URINE_DIP',
      path: 'protein',
      format: 'text',
      subject: 'MOTHER',
    },
  ].sort((a, b) => {
    const keys = [
      'bp',
      'weight',
      'weight_gain',
      'pulse',
      'temp',
      'fh',
      'fht',
      'presentation',
      'movement',
      'edema',
      'urine',
      'urine_other',
      'exam',
      'labs',
      'rhig',
      'education',
      'orders',
    ];
    return keys.indexOf(a.key) - keys.indexOf(b.key);
  }) as FlowsheetColumn[],
};
export function validateDefinition(
  definition: FlowsheetDefinition,
  customDefinitions: import('./customDefinitions').CustomObservationDefinition[] = [],
): string[] {
  const errors: string[] = [];
  if (!definition.id || !Number.isInteger(definition.version) || definition.version < 1)
    errors.push('Invalid definition identity');
  if (new Set(definition.columns.map((c) => c.key)).size !== definition.columns.length)
    errors.push('Duplicate column key');
  if (
    definition.rowStrategy === 'PER_TIMESTAMP_BUCKET' &&
    (!definition.bucketSeconds || !Number.isInteger(definition.bucketSeconds) || definition.bucketSeconds < 1)
  )
    errors.push('Invalid bucket width');
  for (const col of definition.columns) {
    if (col.eventType.startsWith('CUSTOM.')) {
      const d = customDefinitions.find((d) => d.event_type === col.eventType && d.version === col.schemaVersion);
      if (!d || d.subject !== col.subject || col.path !== 'value') errors.push(`Invalid source for ${col.key}`);
      continue;
    }
    const entry = Object.hasOwn(eventCatalog, col.eventType) ? eventCatalog[col.eventType as EventType] : undefined;
    if (
      !entry ||
      !Object.hasOwn(entry.schema.properties, col.path) ||
      !(entry.subjects as readonly string[]).includes(col.subject)
    )
      errors.push(`Invalid source for ${col.key}`);
  }
  return errors;
}
